import React, { useEffect, useState } from "react";
import { Papercontainer } from "@rewardle/apps.ui.papercontainer.papercontainer";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import styled from "@emotion/styled";
import { OutlinedInput as MuiOutlinedInput } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import axios from "axios";
import {
  Table,
  TableBody,
  TableHead,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Typography as MuiTypography,
  Tooltip,
  IconButton
} from "@mui/material";
import { formatTransactionDate } from "../../Utils/commonFunction";

import Loader from "../../components/loader/loader";
import BadgePage from "../../components/badge/BadgePage";
import { statusColor } from "../../Utils/commonConstants";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import { Box, Alert } from "@mui/material";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import { useAppSelector } from "../../redux/hooks";

const CopyToClipboardContainer = CopyToClipboard as any;

const Typography = styled(MuiTypography)``;
const TableCell = styled(MuiTableCell)``;
const TableCellHeading = styled(MuiTableCell)`
  color: var(--light-text-primary, #192f67);
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.17px;
  width: 20px;
`;

const TableCellColumn = styled(MuiTableCell)`
color: var(--light-text-secondary, rgba(0, 0, 0, 0.60));
font-family: Montserrat,sans-serif;
font-size: 14px;
font-style: normal;
font-weight: 400;
line - height: normal;
`;

const TableRow = styled(MuiTableRow)``;

const CustomTableRow = styled(MuiTableRow)`
  &:hover {
    background-color: transparent;
  }
`;

const OutlinedInput = styled(MuiOutlinedInput)`
  height: 36px;
  flex-shrink: 0;
  border: 1px solid #d4d5d6;
  border-radius: 28px;
`;

const CustomTablePagination: any = styled(TablePagination)`
  .MuiTablePagination-selectLabel {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
  }
`;

interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
}

const Transactions = () => {
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };
  const [listOfTransactions, setListOfTransactions] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<any>(10);
  const [copiedTransactionId, setCopiedTransactionId] = useState();
  const [isBusinessIdChange, setIsBusinessIdChange] = useState(false);

  // Global billing / restriction state from Redux
  const { isNonPaying } = useAppSelector((s) => s.businessAccess);

  const handleTransactionDataWithPagination = (
    response: {
      data: { Items: string | any[]; Count: any; LastEvaluatedKey: any };
    },
    page: number,
    rowsPerPage: number
  ) => {
    let oldData = [...listOfTransactions];
    let startIndex = rowsPerPage * (page + 1) - rowsPerPage;
    let endIndex = rowsPerPage * (page + 1);
    const slicedData = response?.data?.Items.slice(startIndex, endIndex);
    let newIndexData = {
      Count: response?.data?.Count,
      Items: slicedData,
      LastEvaluatedKey: response?.data?.LastEvaluatedKey
    };
    oldData.push(newIndexData);
    setListOfTransactions(oldData);
    return null;
  };

  const getTransactionsList = (page = currentPage, pazeSize = rowsPerPage) => {
    setIsLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${
        process.env.REACT_APP_API_BASE_URL
      }/rewardlepay/business/transactions?businessid=${
        state?.currentBusiness?.BusinessId
      }&pagesize=${pazeSize * (page + 1)}`,
      headers: {
        Authorization: "Bearer " + state?.token
      }
    };

    axios
      .request(config)
      .then((response) => {
        handleTransactionDataWithPagination(response, page, rowsPerPage);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (state?.currentBusiness?.BusinessId) {
      setCurrentPage(0);
      setRowsPerPage(10);
      setListOfTransactions([]);
      setIsBusinessIdChange(!isBusinessIdChange);
    }
  }, [state?.currentBusiness?.BusinessId]);

  useEffect(() => {
    if (rowsPerPage) getTransactionsList();
  }, [rowsPerPage, isBusinessIdChange]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
    setListOfTransactions([]); // Clear the current data
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (newPage > currentPage) {
      getTransactionsList(newPage);
    } else {
      let oldData = [...listOfTransactions];
      const slicedData = oldData?.slice(0, newPage + 1);
      setListOfTransactions(slicedData);
    }
    setCurrentPage(newPage);
  };

  const labelDisplayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) => {
    if (listOfTransactions[currentPage]?.LastEvaluatedKey) {
      return `${from}-${to} of more than ${count}`;
    } else {
      return `${from}-${to} of ${count}`;
    }
  };

  const getLastDigitsId = (id: string) => {
    let splitId = id?.split("-");
    return splitId[splitId.length - 1];
  };

  return (
    <React.Fragment>
      <Breadcrumbs
        heading="Transactions"
        subHeading="Manage member transactions"
        docs={null}
      />
      <Papercontainer elevation={0}>
        {isNonPaying && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">
              Access limited: Please renew your contract or fix payment issues
              to view transactions.
            </Alert>
          </Box>
        )}
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="a dense table"
              >
                <TableHead>
                  <TableRow>
                    <TableCellHeading align="left">
                      TransactionId
                    </TableCellHeading>
                    <TableCellHeading align="left">
                      Date & Time
                    </TableCellHeading>
                    <TableCellHeading align="left">Amount</TableCellHeading>
                    <TableCellHeading align="left">
                      Amount Refunded
                    </TableCellHeading>
                    <TableCellHeading align="left">Status</TableCellHeading>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listOfTransactions?.length > 0 ? (
                    listOfTransactions[currentPage]?.Items?.map(
                      (row: any, index: any) => (
                        <CustomTableRow key={index}>
                          <TableCellColumn align="left">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  verticalAlign: "middle",
                                  display: "flex",
                                  alignItems: "left"
                                }}
                              >
                                {getLastDigitsId(row?.TransactionId)}
                              </Box>
                              <Box onClick={(e) => e?.stopPropagation()}>
                                <CopyToClipboardContainer
                                  text={row?.TransactionId}
                                  onCopy={(e: any) =>
                                    setCopiedTransactionId(row?.TransactionId)
                                  }
                                >
                                  <Tooltip
                                    title={
                                      copiedTransactionId === row?.TransactionId
                                        ? "Copied to clipboard!"
                                        : "Copy businessid to clipboard"
                                    }
                                    arrow
                                    placement="bottom-start"
                                  >
                                    <IconButton>
                                      <ContentCopyIcon
                                        sx={{
                                          marginTop: 0,
                                          pt: 0,
                                          width: "0.9rem",
                                          height: "0.9rem"
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </CopyToClipboardContainer>
                              </Box>
                            </Box>
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.LocalTransactionDate
                              ? formatTransactionDate(row?.LocalTransactionDate)
                              : "-"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.TransactionAmount
                              ? `$${(row?.TransactionAmount / 100)?.toFixed(2)}`
                              : "-"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.RefundAmount
                              ? `$${(row?.RefundAmount / 100)?.toFixed(2)}`
                              : "-"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.Status ? (
                              <BadgePage
                                color={
                                  statusColor[row?.Status?.toLowerCase()]?.color
                                }
                              >
                                {statusColor[row?.Status?.toLowerCase()]?.title}
                              </BadgePage>
                            ) : (
                              <BadgePage
                                color={statusColor["successful"]?.color}
                              >
                                {statusColor["successful"]?.title}
                              </BadgePage>
                            )}
                          </TableCellColumn>
                        </CustomTableRow>
                      )
                    )
                  ) : (
                    <TableCell
                      colSpan={3}
                      align="center"
                      style={{ borderBottom: "none" }}
                    >
                      <Typography variant="body1" mt={2}>
                        {"No Data Available"}
                      </Typography>
                    </TableCell>
                  )}
                </TableBody>
              </Table>
            </Box>
            {listOfTransactions?.length > 0 && (
              <CustomTablePagination
                rowsPerPageOptions={[10, 50, 100]}
                component="div"
                count={listOfTransactions[currentPage]?.Count}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                nextIconButtonProps={{
                  disabled: !listOfTransactions[currentPage]?.LastEvaluatedKey
                }}
                labelDisplayedRows={labelDisplayedRows}
              />
            )}
          </>
        )}
      </Papercontainer>
    </React.Fragment>
  );
};

export default Transactions;
